/* Shared card fetcher for Athos-driven carousels. Fetches jam-card markup via ?view=card.
   window.AthosCards.fetchCards(handles, { signal, concurrency, timeoutMs }) -> Promise<string[]>
   of card HTML in handle order; failed cards come back as ''. */
(() => {
  if (window.AthosCards) return;

  const CARD_TIMEOUT_MS = 8000;
  /* One request per product trips the storefront's rate limiting. */
  const DEFAULT_CONCURRENCY = 6;

  /* Aborts on the caller's signal or after ms so a hung request can't wedge a carousel. */
  const withDeadline = (signal, ms) => {
    const controller = new AbortController();
    const onAbort = () => controller.abort(signal?.reason);
    if (signal?.aborted) controller.abort(signal.reason);
    else signal?.addEventListener('abort', onAbort, { once: true });

    const timer = setTimeout(
      () => controller.abort(new DOMException('Card fetch timed out', 'TimeoutError')),
      ms,
    );
    const cleanup = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    };
    return { signal: controller.signal, cleanup };
  };

  const fetchCard = async (handle, { signal, timeoutMs = CARD_TIMEOUT_MS, view = 'card' } = {}) => {
    const { signal: cardSignal, cleanup } = withDeadline(signal, timeoutMs);
    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}?view=${encodeURIComponent(view)}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: cardSignal,
      });
      return response.ok && !response.redirected ? (await response.text()).trim() : '';
    } catch (_error) {
      /* Fail open: a missing or aborted card drops out of the row. */
      return '';
    } finally {
      cleanup();
    }
  };

  const fetchCards = (handles, { signal, concurrency = DEFAULT_CONCURRENCY, timeoutMs, view } = {}) => {
    const run = window.AsyncLimiter
      ? window.AsyncLimiter.createLimiter(concurrency)
      : (task) => task();
    return Promise.all(handles.map((handle) => run(() => fetchCard(handle, { signal, timeoutMs, view }))));
  };

  window.AthosCards = Object.freeze({ fetchCard, fetchCards });
})();
